import React, { useState, useEffect } from 'react';
import { Modal } from '../../../../../components/common/Modal';
import { Button } from '../../../../../components/common/Button';
import { Input, Select } from '../../../../../components/common/FormFields';
import { Copy, Users, Link, Lock, Globe, Mail, Check } from 'lucide-react';
import { generateShareLink } from '../../../../../utils/shareHelpers';
import { logError, logInfo } from '../../../../../utils/logger';
import { CustomizationSection } from './CustomizationSection';
import { TeamShareSection } from './TeamShareSection';
import { Toast } from '../../../../../components/common/Toast';

export const ShareSurveyModal = ({ isOpen, onClose, survey, team = [] }) => {
  const [shareType, setShareType] = useState('public');
  const [shareLink, setShareLink] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [emailInput, setEmailInput] = useState('');
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [toast, setToast] = useState(null);
  const [customizations, setCustomizations] = useState({
    logo: { type: 'default' },
    mapType: 'roadmap',
    interface: {
      showSearch: true,
      showInfoBox: true,
      showMenu: true,
      showLocationButton: true,
      showDriveToButton: true
    },
    display: {
      showSurveyName: true,
      showCustomerName: true,
      showDates: true,
      showVehicleDetails: true,
      showAdditionalDetails: true
    },
    sidebar: {
      enabled: true,
      tabs: {
        basic: true,
        routes: true,
        pois: true,
        share: true,
        files: true,
        settings: true
      }
    }
  });

  useEffect(() => {
    // Reset share link when modal is opened
    if (isOpen) {
      setShareLink('');
      setCopied(false);
    }
  }, [isOpen]);

  const handleGenerateLink = async () => {
    setLoading(true);
    try {
      if (!survey?.id) {
        throw new Error('Survey ID is required');
      }

      const options = {
        type: shareType,
        enableEditing: false,
        customizations,
        selectedUsers: shareType === 'selected' ? [...selectedUsers, ...emails] : []
      };

      const link = await generateShareLink(survey.id, options);
      setShareLink(link);
      logInfo('Share link generated successfully');
    } catch (error) {
      logError('Failed to generate share link:', error);
      setToast({
        type: 'error',
        message: 'Failed to generate share link. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (!shareLink) return;
    
    navigator.clipboard.writeText(shareLink)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        setToast({
          type: 'success',
          message: 'Link copied to clipboard'
        });
      })
      .catch(() => {
        setToast({
          type: 'error',
          message: 'Failed to copy link'
        });
      });
  };

  const handleAddEmail = () => {
    if (!emailInput.trim()) return;
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailInput)) {
      setToast({
        type: 'error',
        message: 'Please enter a valid email address'
      });
      return;
    }
    
    if (!emails.includes(emailInput)) {
      setEmails([...emails, emailInput]);
      setEmailInput('');
    }
  };

  const removeEmail = (email) => {
    setEmails(emails.filter(e => e !== email));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Share Survey">
      <div className="p-6 space-y-6">
        {/* Share Type Selection */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-white">Share Type</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              className={`p-4 rounded-lg flex flex-col items-center gap-2 ${
                shareType === 'public' 
                  ? 'bg-blue-500/20 border-2 border-blue-500' 
                  : 'bg-gray-700 hover:bg-gray-600'
              }`}
              onClick={() => setShareType('public')}
            >
              <Globe className="w-8 h-8 text-blue-400" />
              <span className="text-white font-medium">Public Link</span>
              <span className="text-xs text-gray-400 text-center">Anyone with the link can view</span>
            </button>
            
            <button
              className={`p-4 rounded-lg flex flex-col items-center gap-2 ${
                shareType === 'secret' 
                  ? 'bg-blue-500/20 border-2 border-blue-500' 
                  : 'bg-gray-700 hover:bg-gray-600'
              }`}
              onClick={() => setShareType('secret')}
            >
              <Lock className="w-8 h-8 text-purple-400" />
              <span className="text-white font-medium">Secret Link</span>
              <span className="text-xs text-gray-400 text-center">Secure, unguessable link</span>
            </button>
            
            <button
              className={`p-4 rounded-lg flex flex-col items-center gap-2 ${
                shareType === 'selected' 
                  ? 'bg-blue-500/20 border-2 border-blue-500' 
                  : 'bg-gray-700 hover:bg-gray-600'
              }`}
              onClick={() => setShareType('selected')}
            >
              <Users className="w-8 h-8 text-green-400" />
              <span className="text-white font-medium">Selected Users</span>
              <span className="text-xs text-gray-400 text-center">Share with specific people</span>
            </button>
          </div>
        </div>

        {/* Selected Users Section */}
        {shareType === 'selected' && (
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-white">Share with Users</h3>
            
            {/* Team Members */}
            <TeamShareSection 
              team={team} 
              selectedUsers={selectedUsers} 
              setSelectedUsers={setSelectedUsers} 
            />
            
            {/* Email Invites */}
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-300">Invite by Email</h4>
              <div className="flex gap-2">
                <Input
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  placeholder="Enter email address"
                  className="flex-1 bg-gray-700 border-gray-600 text-white"
                />
                <Button
                  onClick={handleAddEmail}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Add
                </Button>
              </div>
              
              {/* Email List */}
              {emails.length > 0 && (
                <div className="mt-2 space-y-2">
                  {emails.map((email, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-700 rounded-lg">
                      <span className="text-white">{email}</span>
                      <button
                        onClick={() => removeEmail(email)}
                        className="text-gray-400 hover:text-white"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Customization Options */}
        <CustomizationSection 
          customizations={customizations} 
          setCustomizations={setCustomizations} 
        />

        {/* Generate Link Button */}
        <Button
          onClick={handleGenerateLink}
          loading={loading}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white"
        >
          Generate Share Link
        </Button>

        {/* Share Link Display */}
        {shareLink && (
          <div className="bg-gray-700/50 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-white font-medium">Share Link</h3>
              <Button
                onClick={handleCopy}
                className="bg-gray-600 hover:bg-gray-500 text-white"
                size="small"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
            <div className="bg-gray-800 p-2 rounded text-gray-300 break-all">
              {shareLink}
            </div>
          </div>
        )}
      </div>

      {/* Toast Notifications */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </Modal>
  );
};

export default ShareSurveyModal;